import LoadingContent from "@/common/LoadingContent";
import { Actions as COMPANY_ACTIONS } from "@/redux/actions/companyActions";
import { Actions as EMPLOYEEACTIONS } from "@/redux/actions/employeeActions";
import { Actions as PAYMENT_ACTIONS } from "@/redux/actions/paymentActions";
import { ArrowDownTrayIcon, ArrowsRightLeftIcon, MinusIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { DateInputFormat } from "@/components/common/DateComponents";
import { dateToString } from "@/components/common/validation/Validators";
import { addMonths, format, subYears } from "date-fns";
import _ from "lodash";
import { useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import { connect } from "react-redux";
import BillingRow from "./BillingRow";

const Billing = ({
	userPermissions,
	isAdmin,
	payPeriodDetails,
	setBillingModal,
	org,
	bills,
	businessDetails,
	hasSelectedCredits,
}) => {
	const hasPendingBills = useMemo(() => {
		if (bills) {
			return bills.filter((bill) => bill.billingStatus === "Pending").length > 0;
		}
	}, [bills]);

	const thisYear = new Date().getFullYear();
	const [startDate, setStartDate] = useState(new Date(thisYear, 0, 1));
	const [endDate, setEndDate] = useState(new Date(thisYear, 11, 31));
	const [maxDate, setMaxDate] = useState(new Date());

	if (userPermissions === "None") return null;

	if (!org) return <LoadingContent />;

	return (
		<>
			<div className="content">
				<div className="block-header">
					<h3 className="block-title">Credit History</h3>
					{isAdmin ? (
						<>
							<div
								className="btn btn-default pull-right"
								style={{ marginRight: 5 }}
								onClick={() => setBillingModal("Transfer")}
							>
								<ArrowsRightLeftIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Transfer Credits</div>
							</div>
							<div
								className="btn btn-default pull-right"
								style={{ marginRight: 5 }}
								onClick={() => setBillingModal("Invoice")}
							>
								<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Issue Credits</div>
							</div>
							<div
								className="btn btn-default pull-right"
								style={{ marginRight: 5 }}
								onClick={() => setBillingModal("Transaction")}
							>
								<MinusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>Issue Transaction</div>
							</div>
						</>
					) : null}
					<div className="clearfix" />
				</div>

				<div className="block-content">
					<div className="col-xs-2 pl-0">
						<label>From</label>
						<DatePicker
							selected={startDate}
							onChange={(date) => {
								if (date === null) setStartDate(null);
								if (date) setStartDate(date);
							}}
							minDate={subYears(new Date(), 100)}
							maxDate={maxDate}
							isClearable
							wrapperClassName="full-width"
							showYearDropdown
							dateFormat="dd/MM/yyyy"
							customInput={<DateInputFormat />}
						/>
						<div className="clearfix" />
					</div>
					<div className="col-xs-2">
						<label htmlFor=" ">To</label>
						<DatePicker
							selected={endDate}
							onChange={(date) => {
								if (date === null) setEndDate(null);
								if (
									date &&
									startDate &&
									format(date, "yyyy-MM-dd") <= format(startDate, "yyyy-MM-dd") &&
									format(date, "yyyy") == format(startDate, "yyyy")
								) {
									setStartDate(date);
								}
								setEndDate(date);
							}}
							minDate={subYears(new Date(), 100)}
							wrapperClassName="full-width"
							maxDate={maxDate}
							dateFormat="dd/MM/yyyy"
							isClearable
							showYearDropdown
							customInput={<DateInputFormat />}
						/>
					</div>
					<div
						className="col-xs-8 pr-0 pl-0"
						style={{ marginTop: 25 }}
					>
						{isAdmin ? (
							<>
								{
									// only show if admin; and there are pending bills; and there are selected credits; and the company is linked to an advisor
									businessDetails?.advisor && (
										<button
											className="btn btn-default pull-right"
											type="button"
											onClick={() => setBillingModal("TransferCreditsToAdvisor")}
											disabled={!hasPendingBills || !hasSelectedCredits}
										>
											<ArrowsRightLeftIcon
												style={{ height: "1.5em", float: "left", marginRight: 5 }}
											/>
											<div style={{ float: "left" }}>Transfer to Advisor</div>
										</button>
									)
								}
								<button
									className="btn btn-default pull-right"
									type="button"
									style={{ marginRight: 5 }}
									onClick={() => setBillingModal("VoidCredits")}
									disabled={!hasPendingBills || !hasSelectedCredits}
								>
									<TrashIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
									<div style={{ float: "left" }}>Void Transactions</div>
								</button>
							</>
						) : null}
					</div>
					<div className="clearfix" />

					<table className="table table-responsive">
						<thead>
							<tr>
								{isAdmin && hasPendingBills && <th> </th>}
								<th>Date</th>
								<th>Transaction</th>
								<th>Balance</th>
								<th>Details</th>
								<th>
									<CSVLink
										title="Download Transaction History"
										filename="TransactionHistory.csv"
										data={[
											["Date", "Old Balance", "Transaction", "New Balance", "Invoice", "Details"],
											...bills
												.filter((bill) =>
													startDate
														? dateToString(new Date(bill.date)) >= dateToString(startDate)
														: bill && endDate
														? dateToString(new Date(bill.date)) <= dateToString(endDate)
														: bill,
												)
												?.map((bill) => {
													const date = format(new Date(bill?.date), "dd/MM/yyyy");
													const transaction = parseFloat(
														`${bill.type == "Credit" ? "+" : "-"}${bill?.credits}`,
													);
													const balance = parseFloat(bill?.balance);
													const description = bill?.description ? bill?.description : bill.type == "Credit" ? "Credit Purchase" : "";

													return [
														date,
														transaction * -1 + balance,
														transaction,
														balance,
														bill?.invoiceNo || "",
														description,
													];
												}),
										]}
										className="btn btn-xs btn-default"
										type="button"
										style={{ marginRight: 10 }}
									>
										<ArrowDownTrayIcon
											className="h-5 w-5 text-dark-gray"
											style={{ marginTop: "3px", verticalAlign: "sub" }}
										/>
										Credit History
									</CSVLink>
								</th>
							</tr>
						</thead>
						<tbody>
							{bills
								?.filter((bill) =>
									startDate
										? dateToString(new Date(bill.date)) >= dateToString(startDate)
										: bill && endDate
										? dateToString(new Date(bill.date)) <= dateToString(endDate)
										: bill,
								)
								?.map((bill) => {
									const periodDetails =
										payPeriodDetails &&
										payPeriodDetails.filter((item) => item.payPeriodId == bill.payPeriod);
									return (
										<BillingRow
											key={bill.id}
											bill={bill}
											payPeriodDetails={periodDetails}
											hasPendingBills={hasPendingBills}
										/>
									);
								})}
						</tbody>
						<tfoot>
							<tr>
								<th>
									Showing 1-{bills.length} of {bills.length}
								</th>
								<th colSpan="4" />
								{/* <th style={{ textAlign: "right" }}>
                                    &lt; 1 &gt;
                                </th> */}
							</tr>
						</tfoot>
					</table>
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	const { creditBalance, activeEmployees, creditsSpent, advisorDetails } = state.payment?.stats || {};

	const businessDetails = state?.company?.settings;

	const userPermissions = state?.permissions?.my?.permissions?.billing;
	const bundles =
		state.payment.bundles &&
		_.orderBy(
			state.payment.bundles.filter((b) => b.isPlan),
			["credits"],
			["asc"],
		);

	const currency = state.company?.settings?.currency;
	const planOptions = bundles?.map((item) => ({
		...item,
		value: item.id,
		label: `${item.name} - ${currency}${item.price}/employee/mo`,
	}));
	const selectedPlan = planOptions?.find((item) => item.value == state.company?.settings?.plan);

	const usesAdvisor =
		businessDetails?.advisor && businessDetails.paidByAdvisor && businessDetails?.advisor === advisorDetails?.id;

	return {
		bills: state.payment.credits || [],
		hasSelectedCredits: state.payment.hasSelectedCredits,
		payPeriodDetails: state.payPeriods?.periods,
		creditBalance,
		activeEmployees,
		payrollsRun: state.payPeriods?.periods?.length,
		creditsSpent: "-",
		userPermissions,
		isAdmin: state?.my?.profile?.isAdmin,
		org: state?.company?.settings.org,
		businessDetails,
		advisorDetails: usesAdvisor ? advisorDetails : {},
		currency,
		refCode: String(
			usesAdvisor ? advisorDetails?.billingReference : businessDetails?.billingReference,
		).toUpperCase(),
		orgId: state?.company?.settings?.org,
		employeeListLength: state?.employees?.employeeListWithSalary?.length,
		selectedPlan,
		advisorPlan: usesAdvisor ? advisorDetails?.plan : undefined,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getCredits: ({ before, after }) => dispatch(PAYMENT_ACTIONS.getCredits({ before, after })),
	getPaymentStats: () => dispatch(PAYMENT_ACTIONS.getPaymentStats()),
	showPricingModal: () => dispatch(PAYMENT_ACTIONS.showPricingModal(true)),
	setBillingModal: (billingModal) => dispatch(COMPANY_ACTIONS.setBillingModal(billingModal)),
	employeesGetListWithSalary: ({ from, to }) =>
		dispatch(
			EMPLOYEEACTIONS.employeesGetListWithSalary(
				{
					from: from ? from : format(addMonths(new Date(), -2), "yyyy-MM-01"), // get list from last 2 months
					to: to ? to : format(new Date(), "yyyy-12-31"),
				},
				{ includeTax: true },
			),
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(Billing);
